import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn, formatTime } from '@/lib/utils';
import { BellRing, Check, ChevronDown, Clock, Link2, PhoneCall } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { recommendAction, ACTIONS } from '@/lib/recommendAction';
import { t } from '@/i18n';
import { at, getCarrier } from '@/data/arrivalsData';
import { stateStyles, stateLabel } from './arrivalsUi';
import { RiskBadge } from './RiskBadge';

const icons = { urge: BellRing, call: PhoneCall, link: Link2, replan: Clock, resolve: Check };

// One loading in the work queue: one recommended action as the main button (#118),
// the rest under "More…", "Resolved" always at hand.
export const ActionItem = ({ loading, risk, onUrge, onCall, onLink, onReplan, onResolve }) => {
  const carrier = getCarrier(loading.carrierId);
  const [form, setForm] = useState(null); // 'replan' | 'resolve'
  const [time, setTime] = useState(formatTime(loading.plannedAt));
  const [note, setNote] = useState('');
  const { action: recommended, reason } = recommendAction(loading);
  const run = {
    urge: () => onUrge(loading.id),
    call: () => onCall(loading.id),
    link: () => onLink(carrier),
    replan: () => setForm(form === 'replan' ? null : 'replan'),
    resolve: () => setForm(form === 'resolve' ? null : 'resolve'),
  };
  const Main = icons[recommended];

  return (
    <Card className={cn('p-3 space-y-2 border-l-4', risk?.level === 'high' ? 'border-l-red-500' : 'border-l-amber-400')} data-testid={`action-${loading.id}`}>
      <div className="flex items-start gap-3">
        <div className="text-center shrink-0 w-12">
          <div className="text-sm font-bold tabular-nums">{formatTime(loading.plannedAt)}</div>
          <div className="text-[10px] text-muted-foreground">{t('arrivals.plan')}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{loading.plate}</span>
            <Badge variant="outline" className={cn('text-[11px]', stateStyles[loading.state])}>{stateLabel(loading.state)}</Badge>
            {risk && <RiskBadge risk={risk} />}
            {loading.urged && <Badge variant="outline" className="text-[11px] gap-1"><Clock className="h-3 w-3" /> {t('actions.urged')}</Badge>}
          </div>
          <div className="text-xs text-muted-foreground truncate">{carrier.name} · {carrier.dispatcher.name} · {loading.orderId}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Button size="sm" className="h-8 gap-1 text-xs" onClick={run[recommended]}><Main className="h-3.5 w-3.5" /> {t(`actions.${recommended}`)}</Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="h-8 gap-1 text-xs">{t('actions.more')} <ChevronDown className="h-3 w-3" aria-hidden="true" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {ACTIONS.filter(a => a !== recommended && a !== 'resolve').map(a => {
              const Icon = icons[a];
              return <DropdownMenuItem key={a} onSelect={run[a]} className="gap-2 text-sm"><Icon className="h-3.5 w-3.5" /> {t(`actions.${a}`)}</DropdownMenuItem>;
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs ml-auto" onClick={run.resolve}><Check className="h-3.5 w-3.5" /> {t('actions.resolve')}</Button>
      </div>
      <div className="text-xs text-muted-foreground">{t('actions.recommended')}: {t(`actions.reason.${reason}`)}</div>

      {form === 'replan' && (
        <div className="flex items-center gap-2">
          <Input type="time" aria-label={t('actions.newTime')} value={time} onChange={e => setTime(e.target.value)} className="h-8 w-32 text-sm" />
          <Button size="sm" className="h-8" onClick={() => onReplan(loading.id, { eta: at(time), summary: `${t('actions.replannedTo')} ${time}` })}>{t('actions.confirm')}</Button>
        </div>
      )}
      {form === 'resolve' && (
        <div className="flex items-center gap-2">
          <Input aria-label={t('actions.note')} placeholder={t('actions.notePlaceholder')} value={note} onChange={e => setNote(e.target.value)} className="h-8 text-sm" />
          <Button size="sm" className="h-8" onClick={() => onResolve(loading.id, `${t('actions.resolvedNote')}: ${note || '—'}`)}>{t('actions.confirm')}</Button>
        </div>
      )}
    </Card>
  );
};
